/**
 * Basemap providers for the map controls.
 *
 * Three renderers sit behind one component contract (see components/map):
 *   raster — Leaflet + an XYZ tile URL (osm, here, maptiler)
 *   mapbox — Mapbox GL JS, a style URL
 *   google — Google Maps JavaScript API, a mapTypeId
 * Raster providers are interchangeable by URL alone; the other two each need
 * their own renderer because neither publishes a usable raster XYZ endpoint.
 */

export type MapRenderer = 'raster' | 'mapbox' | 'google';

/** One selectable basemap look within a provider — a runtime user choice. */
export interface BasemapVariant {
  id: string;
  label: string;
  /**
   * Renderer-specific handle for this look:
   *   raster — XYZ tile template
   *   mapbox — style URL
   *   google — mapTypeId ('roadmap' | 'satellite' | 'hybrid' | 'terrain')
   */
  url: string;
  /** Mapbox renderer: camera pitch in degrees. 3D styles render flat at 0. */
  pitch?: number;
}

export interface MapTileProvider {
  id: string;
  label: string;
  /** Which component renders it — raster providers share the Leaflet picker. */
  renderer: MapRenderer;
  /** Looks the user can switch between. The first entry is the default. */
  variants: BasemapVariant[];
  attribution: string;
  subdomains?: string;
  maxZoom?: number;
  /** Env var holding the key; when set but empty the provider is skipped. */
  keyEnv?: string;
}

const KEYS = {
  here:     import.meta.env.VITE_HERE_API_KEY as string | undefined,
  mapbox:   import.meta.env.VITE_MAPBOX_TOKEN_BROWSER as string | undefined,
  maptiler: import.meta.env.VITE_MAPTILER_KEY as string | undefined,
};

export const MAP_PROVIDERS: Record<string, MapTileProvider> = {
  osm: {
    id: 'osm',
    renderer: 'raster',
    label: 'OpenStreetMap',
    subdomains: 'abc',
    maxZoom: 19,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    variants: [
      { id: 'streets', label: 'Streets', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
    ],
  },

  here: {
    id: 'here',
    renderer: 'raster',
    label: 'HERE',
    keyEnv: 'VITE_HERE_API_KEY',
    maxZoom: 20,
    attribution: '&copy; HERE',
    variants: [
      {
        id: 'explore',
        label: 'Streets',
        url:
          'https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8' +
          `?style=explore.day&size=512&apiKey=${KEYS.here ?? ''}`,
      },
      {
        id: 'satellite',
        label: 'Satellite',
        url:
          'https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/jpeg' +
          `?style=explore.satellite.day&size=512&apiKey=${KEYS.here ?? ''}`,
      },
    ],
  },

  mapbox: {
    id: 'mapbox',
    renderer: 'mapbox',
    label: 'Mapbox GL',
    keyEnv: 'VITE_MAPBOX_TOKEN_BROWSER',
    maxZoom: 22,
    attribution: '&copy; Mapbox &copy; OpenStreetMap',
    variants: [
      { id: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
      { id: 'streets',   label: 'Streets',   url: 'mapbox://styles/mapbox/streets-v12' },
      // Outdoors carries contours and shaded relief — useful for judging ground
      // before a reconstruction. Kept top-down: a pitched camera would distort
      // the extent rectangle, which is the one thing this control has to show.
      { id: 'terrain',   label: 'Terrain',   url: 'mapbox://styles/mapbox/outdoors-v12' },
    ],
  },

  google: {
    id: 'google',
    renderer: 'google',
    label: 'Google Maps',
    keyEnv: 'VITE_GOOGLE_MAPS_KEY_BROWSER',
    // Google caps zoom by mapTypeId and location; the renderer clamps rather
    // than requesting a level the imagery does not have.
    maxZoom: 21,
    // Google draws its own logo and "Map data ©" line into the canvas and
    // neither can be removed, so nothing extra is layered on top.
    attribution: '',
    variants: [
      // 'hybrid' rather than 'satellite': imagery *with* road and place labels,
      // which is what the Mapbox satellite-streets default gave us.
      { id: 'satellite', label: 'Satellite', url: 'hybrid' },
      { id: 'streets',   label: 'Streets',   url: 'roadmap' },
      { id: 'terrain',   label: 'Terrain',   url: 'terrain' },
    ],
  },

  maptiler: {
    id: 'maptiler',
    renderer: 'raster',
    label: 'MapTiler',
    keyEnv: 'VITE_MAPTILER_KEY',
    maxZoom: 20,
    attribution: '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; OpenStreetMap',
    variants: [
      { id: 'streets',   label: 'Streets',   url: `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${KEYS.maptiler ?? ''}` },
      { id: 'satellite', label: 'Satellite', url: `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${KEYS.maptiler ?? ''}` },
    ],
  },
};

export const AVAILABLE_MAP_PROVIDERS = Object.keys(MAP_PROVIDERS);

function keyFor(p: MapTileProvider): string | undefined {
  if (!p.keyEnv) return undefined;
  return (import.meta.env as Record<string, string | undefined>)[p.keyEnv];
}

/**
 * Resolves VITE_MAP_PROVIDER. Unknown names, or providers whose key is missing,
 * fall back to OSM with a console warning — a bad env value degrades the map
 * rather than breaking the page.
 */
export function getMapProvider(): MapTileProvider {
  const requested = (import.meta.env.VITE_MAP_PROVIDER as string | undefined)?.trim() || 'osm';
  const provider = MAP_PROVIDERS[requested];

  if (!provider) {
    console.warn(
      `[Map] Unknown provider "${requested}". Known: ${AVAILABLE_MAP_PROVIDERS.join(', ')}. Using osm.`,
    );
    return MAP_PROVIDERS.osm;
  }
  if (provider.keyEnv && !keyFor(provider)?.trim()) {
    console.warn(`[Map] Provider "${requested}" needs ${provider.keyEnv}, which is not set. Using osm.`);
    return MAP_PROVIDERS.osm;
  }
  return provider;
}

/**
 * Leaflet-only call sites (the legacy scene-creation MapPicker) need a raster
 * tile URL, which the mapbox and google entries do not have. Rather than let
 * those call sites render a mapTypeId as a tile template, hand them OSM.
 */
export function getRasterMapProvider(): MapTileProvider {
  const provider = getMapProvider();
  if (provider.renderer === 'raster') return provider;
  console.warn(
    `[Map] Provider "${provider.id}" has no raster tiles; this Leaflet control falls back to osm.`,
  );
  return MAP_PROVIDERS.osm;
}

/*
 * Billing note
 * ------------
 * Mapbox GL JS and the Google Maps JS API are both billed per MAP LOAD — one
 * initialisation, after which panning and refitting are free. The raster entries
 * (osm, here, maptiler) bill per TILE REQUEST, which our extent slider multiplies
 * quickly. That is why neither GL provider is expressed as a raster tile URL.
 *
 * Key note
 * ---------
 * Browser keys ship inside the bundle by design and are restricted at the
 * provider: VITE_MAPBOX_TOKEN_BROWSER by URL, VITE_GOOGLE_MAPS_KEY_BROWSER by
 * HTTP referrer plus an API restriction. There is one per environment, and they
 * are not interchangeable — a production token's allowlist excludes localhost.
 *
 * The server-side geocoder uses entirely separate credentials in
 * ydrive-customer (MAPBOX_TOKEN_SERVER, GOOGLE_MAPS_KEY_SERVER), which are not
 * checked in. Never reuse a browser key server-side: a referrer-restricted key
 * fails from Node, which sends no referrer.
 */
